import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import { Shield, Trash2 } from 'lucide-react';
import { DashboardProject } from '../../../../types/project';
import type { PipeSize, SetupState, SetupState1, PipeSize1, FabricationSetupData } from '../../../../types/setup';
import { mockBulkHeadSetup } from '../../../../mocks/setup';
import { CommonSetupForm } from './CommonSetupForm';
import { NumberInput } from '../../../../../../components/ui/NumberInput';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';

interface BulkHeadSetupProps {
  project: DashboardProject;
  manufacturingList?: any[];
  setManufacturingList: React.Dispatch<React.SetStateAction<any[]>>;
  unit: string;
  setUnit?: (unit: string) => void;
  showValidation?: boolean;
  setupData?: FabricationSetupData;
  state?: SetupState1;
  setState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  canEdit?: boolean;
  onSavePipe?: (pipeIndex: number) => Promise<boolean>;
}

export interface BulkHeadSetupRef {
  getSetupData: () => SetupState1;
}

export const BulkHeadSetup = forwardRef<BulkHeadSetupRef, BulkHeadSetupProps>(({ project, manufacturingList, setManufacturingList, unit, setUnit, showValidation = false, setupData, state: propState, setState: propSetState, setHasUnsavedChanges, canEdit = true, onSavePipe }, ref) => {
  // Use prop state if provided, otherwise use internal state
  const [internalState, setInternalState] = useState<SetupState1>({
    numberOfPipeSizes: 1,
    projectId: parseInt(project.project.projectId),
    projectTypeId: project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id || 0,
    projectType: 'fabrication',
    projectSubType: 'bulkhead',
    pipeSizes: [{
      pipeId: 0,
      pipeName: "Pipe 1",
      pipeSize: 0,
      wallThickness: 0,
      numberOfBuckleArrestors: 0,
      numberOfSpools: 0,
      numberOfPipes: 0,
      pipeLength: 0,
      numberOfForges: 0,
      outputAssemblyLength: 0,
      manufacturerId: 0,
      manufacturerIds: [],
      pipeOrder: 1,
      isActive: true,
      prefixAssemblyName: 'Assy',
      groupId: '',
      jobNumber: {
        numberOfJobs: 1,
        jobNumbers: ['']
      },
      documentsData: {
        numberOfDocs: 0,
        isDocumentsAvailable: false,
        documentIds: [],
        documentFileNames: []
      }
    }]
  });

  // Use prop state if provided, otherwise use internal state
  const state = propState || internalState;
  const setState = propSetState || setInternalState;

  // State for buckle arrestor reduction modal
  const [isBuckleArrestorModalVisible, setIsBuckleArrestorModalVisible] = useState(false);
  const [pendingBuckleArrestorChange, setPendingBuckleArrestorChange] = useState<{
    value: string;
    index: number;
  } | null>(null);



  // Expose setup data through ref
  useImperativeHandle(ref, () => ({
    getSetupData: () => state
  }));

  const handleConfirmBuckleArrestorChange = () => {
    if (pendingBuckleArrestorChange) {
      const num = parseInt(pendingBuckleArrestorChange.value) || 0;
      setState(prev => ({
        ...prev,
        pipeSizes: prev.pipeSizes.map((p, i) =>
          i === pendingBuckleArrestorChange.index ? { ...p, numberOfBuckleArrestors: num } : p
        )
      }));
    }
    setIsBuckleArrestorModalVisible(false);
    setPendingBuckleArrestorChange(null);
  }

  const handleCancelBuckleArrestorChange = () => {
    setIsBuckleArrestorModalVisible(false);
    setPendingBuckleArrestorChange(null);
    // Don't apply any changes - just close the modal
  }

  // Validation function for additional fields
  const validateField = (value: any): boolean => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return false;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }
    return true;
  };

  const additionalFields = (pipe: PipeSize1, index: number, helpers?: {
    handleUnitChange: (index: number, field: 'pipeSize' | 'wallThickness' | 'length' | 'outputAssemblyLength', value: string) => void;
    UNIT_OPTIONS: { value: string; label: string }[];
    canEdit?: boolean;
    showValidation?: boolean;
    validateField?: (value: any) => boolean;
  }) => (
    <>
      <div>
        <Label className="text-[12px] font-medium text-text-grey" htmlFor={`buckleArrestors-${index}`}>Number of Buckle Arrestors</Label>
        <NumberInput
          id={`buckleArrestors-${index}`}
          min={0}
          step={1}
          value={pipe.numberOfBuckleArrestors || 0}
          onChange={(value) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            const num = parseInt(value) || 0;
            const currentPipe = state.pipeSizes[index];
            const currentBuckleArrestors = currentPipe.numberOfBuckleArrestors || 0;

            // Check if we're reducing the number of buckle arrestors
            if (num < currentBuckleArrestors) {
              // Check if there's any data that would be affected
              const hasProduction = (currentPipe as any).productionData?.isProductionAvailable;
              const hasMaterials = (currentPipe as any).materialsData?.isMaterialsAvailable;
              const hasDocuments = currentPipe.documentsData?.isDocumentsAvailable;

              if (hasProduction || hasMaterials || hasDocuments) {
                // Store the pending change and show modal
                setPendingBuckleArrestorChange({ value, index });
                setIsBuckleArrestorModalVisible(true);
                return;
              }
            }

            // If no data conflicts or increasing, proceed with the change
            setState(prev => ({
              ...prev,
              pipeSizes: prev.pipeSizes.map((p, i) =>
                i === index ? { ...p, numberOfBuckleArrestors: num } : p
              )
            }));
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            const value = e.target.value;
            const num = parseInt(value) || 0;
            const currentPipe = state.pipeSizes[index];
            const hasProduction = (currentPipe as any).productionData?.isProductionAvailable;

            // If there's production data and user is trying to reduce below production count
            if (hasProduction && currentPipe.productionData?.numberOfProductions &&
              currentPipe.productionData?.numberOfProductions > num) {
              // Show modal and restore original value
              setPendingBuckleArrestorChange({ value: currentPipe.productionData.numberOfProductions.toString(), index });
              setIsBuckleArrestorModalVisible(true);
              const forced =
                currentPipe.productionData?.numberOfProductions ||
                0;
              setState((prev) => ({
                ...prev,
                pipeSizes: prev.pipeSizes.map((p, i) =>
                  i === index
                    ? { ...p, numberOfBuckleArrestors: forced }
                    : p
                ),
              }));
            }
          }}
          className={`mt-2 ${showValidation && !validateField(pipe.numberOfBuckleArrestors) ? '' : ''}`}
          placeholder="Enter number of buckle arrestors"
          disabled={!canEdit}
        />
      </div>
      <div>
        <Label className="text-[12px] font-medium text-text-grey" htmlFor={`outputAssemblyLength-${index}`}>Expected Output Assembly Length</Label>
        <NumberInput
          id={`outputAssemblyLength-${index}`}
          min={0}
          step={0.01}
          value={pipe.outputAssemblyLength}
          onChange={(value) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            setState(prev => ({
              ...prev,
              pipeSizes: prev.pipeSizes.map((p, i) =>
                i === index ? { ...p, outputAssemblyLength: parseFloat(value) || 0 } : p
              )
            }));
          }}
          {...(helpers?.UNIT_OPTIONS && helpers?.handleUnitChange
            ? {
                showUnitSelect: true,
                unitValue: pipe.units?.outputAssemblyLength || unit,
                unitOptions: helpers.UNIT_OPTIONS,
                onUnitChange: (v: string) => helpers.handleUnitChange(index, 'outputAssemblyLength', v),
                unitSelectWrapperClassName: 'mt-2',
                className:
                  showValidation && !validateField(pipe.outputAssemblyLength) ? 'border-red-500' : '',
              }
            : {
                className: `mt-2 ${showValidation && !validateField(pipe.outputAssemblyLength) ? 'border-red-500' : ''}`,
              })}
          placeholder="Enter expected output assembly length"
          disabled={!canEdit}
        />
      </div>
      <div>
        <Label className="text-[12px] font-medium text-text-grey" htmlFor={`prefixAssemblyName-${index}`}>Assembly Name (Prefix) <span className="text-red-500">*</span></Label>
        <Input
          id={`prefixAssemblyName-${index}`}
          value={pipe.prefixAssemblyName || ''}
          onChange={(e) => {
            if (!canEdit) return; // Prevent changes when canEdit is false
            setState(prev => ({
              ...prev,
              pipeSizes: prev.pipeSizes.map((p, i) =>
                i === index ? { ...p, prefixAssemblyName: e.target.value } : p
              )
            }));
            setHasUnsavedChanges(true);
          }}
          className={`mt-2 ${showValidation && !validateField(pipe.prefixAssemblyName) ? '' : ''}`}
          placeholder="Enter prefix assembly name"
          disabled={!canEdit}
        />
        {showValidation && !validateField(pipe.prefixAssemblyName) && (
          <p className="mt-1 text-xs text-red-500">Prefix assembly name is required</p>
        )}
      </div>
    </>
  );

  return (
    <>
      <CommonSetupForm
        project={project}
        state={state}
        setState={setState}
        title="Bulk Head"
        icon={<Shield className="h-5 w-5" />}
        iconColor="text-purple-800"
        additionalFields={additionalFields as any}
        manufacturingList={manufacturingList}
        setManufacturingList={setManufacturingList}
        unit={unit}
        setUnit={setUnit}
        showValidation={showValidation}
        setHasUnsavedChanges={setHasUnsavedChanges}
        onSavePipe={onSavePipe}
        canEdit={canEdit}
      />

      {/* Buckle Arrestor Reduction Confirmation Dialog */}
      {/* Buckle Arrestor Reduction Confirmation Dialog */}
      <Dialog open={isBuckleArrestorModalVisible} onOpenChange={() => {
        handleCancelBuckleArrestorChange();
      }}>
        <DialogContent className="max-w-[700px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              Reduce Buckle Arrestors
            </DialogTitle>
          </div>

          {(() => {
            if (!pendingBuckleArrestorChange) return null;

            const currentPipe = state.pipeSizes[pendingBuckleArrestorChange.index];
            const currentBuckleArrestors = currentPipe.numberOfBuckleArrestors || 0;
            const newBuckleArrestors = parseInt(pendingBuckleArrestorChange.value) || 0;
            const assembliesToRemove = currentBuckleArrestors - newBuckleArrestors;

            const hasProduction = (currentPipe as any).productionData?.isProductionAvailable;

            const affectedData = [];
            if (hasProduction) affectedData.push('production data');


            return (
              <div>


                <div className="mb-3">
                  <p className="text-600 mb-2">⚠️ Cannot decrease the number of buckle arrestors for  <span className="font-semibold text-red-600">{currentPipe.pipeName}</span> because the buckle arrestors have associated production data.</p>

                </div>

                <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
                  <p className="text-sm text-yellow-700">
                    <span className="font-semibold">Action Required:</span>
                    <br />
                    <span className="block mt-1">• Please remove all associated production data before decreasing the number of buckle arrestors.</span>

                  </p>
                </div>


              </div>
            );
          })()}

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleCancelBuckleArrestorChange}
            >
              Cancel
            </Button>

          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}); 